// Renovation Cost Estimator – Girona (Catalonia, Spain) – prototype methodology
// Budget for façade and windows works from the COAC reference budget, and the EPC rating
// projected after the works from the IDAE rating scale and two energy-saving scenarios.

import { round } from "../components/simulatorRounding";

// ---------------------------------------------------------------------------
// Parameters (see the methodology appendix)
// ---------------------------------------------------------------------------
export const GIRONA_RENOVATION_PARAMS = {
  version: "COAC basic module 2024 (646 €)",
  // COAC reference budget: cost per m² of façade = Mb × Cg × Ct × Cq × Cu
  coac: {
    basicModule: 646, // Mb, in force from 1 January 2024 – check every January
    geographic: 0.95, // Cg, province of Girona
    typeOfWorks: 0.5, // Ct, façade rehabilitation with replacement of joinery or enclosures
    quality: 1.0, // Cq, standard level of finishes
    // Cu by floor area of the dwelling: below 50; 50–99; 100–149; 150–200; above 200 m²
    use: [
      { below: 50, value: 1.8 },
      { below: 100, value: 1.4 },
      { below: 150, value: 1.6 },
      { upTo: 200, value: 1.8 },
      { value: 2.0 },
    ],
  },
  // Façade area per m² of floor area (IDAE typologies 10.1 and 8.1, two façades, 3 m storeys)
  facadeRatio: { apartment: 0.39, house: 0.68 },
  // IDAE scale, climate zone C2 (kWh/m² per year of non-renewable primary energy)
  epcScale: {
    limitRatios: [0.37, 0.6, 0.93, 1.43], // A/B, B/C, C/D, D/E × new-building reference
    apartment: { newBuildingReference: 61.5 + 6.5 + 8.2, existingStockAverage: 209.3, existingDispersion: 1.15 },
    house: { newBuildingReference: 92.4 + 10.0 + 11.1, existingStockAverage: 253.3, existingDispersion: 1.2 },
  },
  scenarios: { conservative: 0.3, standard: 0.45 }, // Real Decreto 853/2021, Table 15.1
};

export const PROPERTY_TYPES = ["apartment", "house"];
export const RATINGS = ["A", "B", "C", "D", "E", "F", "G"];
export const CURRENT_RATINGS = ["B", "C", "D", "E", "F", "G"];

// ---------------------------------------------------------------------------
// EPC scale: limits between letters and representative consumption per letter
// ---------------------------------------------------------------------------
export function epcScale(propertyType, params = GIRONA_RENOVATION_PARAMS) {
  const { limitRatios } = params.epcScale;
  const ref = params.epcScale[propertyType];
  const Is = ref.existingStockAverage;
  const R = ref.existingDispersion;

  // Upper limits of A, B, C, D, E and F (G has none), to one decimal as published
  const limits = [
    ...limitRatios.map((ratio) => round(ratio * ref.newBuildingReference, 1)),
    round(Is, 1),
    round((Is * (1 + 2 * (R - 1))) / R, 1),
  ];

  // Middle of each band; A: half of its upper limit; G: F/G limit + half the width of F
  const representative = {};
  RATINGS.forEach((rating, i) => {
    let value;
    if (i === 0) value = limits[0] / 2;
    else if (i === RATINGS.length - 1) value = limits[i - 1] + (limits[i - 1] - limits[i - 2]) / 2;
    else value = (limits[i - 1] + limits[i]) / 2;
    representative[rating] = round(value, 1);
  });

  return { limits, representative };
}

// Rating whose band contains the consumption (lower limit inclusive)
export function ratingFor(consumption, propertyType, params = GIRONA_RENOVATION_PARAMS) {
  const { limits } = epcScale(propertyType, params);
  const index = limits.findIndex((upper) => consumption < upper);
  return index === -1 ? "G" : RATINGS[index];
}

export function dwellingUseCoefficient(floorArea, params = GIRONA_RENOVATION_PARAMS) {
  const band = params.coac.use.find(
    (b) => (b.below !== undefined && floorArea < b.below) || (b.upTo !== undefined && floorArea <= b.upTo) || (b.below === undefined && b.upTo === undefined)
  );
  return band.value;
}

// ---------------------------------------------------------------------------
// Estimator
// ---------------------------------------------------------------------------
export function estimateRenovationGirona({
  propertyType,
  floorArea,
  currentRating,
  currentConsumption,
  params = GIRONA_RENOVATION_PARAMS,
}) {
  const area = Number(floorArea);
  if (!PROPERTY_TYPES.includes(propertyType) || !(area > 0) || !CURRENT_RATINGS.includes(currentRating)) {
    return null;
  }

  // 1–3. Façade area, cost per m² of façade and budget (nearest 100 €)
  const { basicModule, geographic, typeOfWorks, quality } = params.coac;
  const cu = dwellingUseCoefficient(area, params);
  const costPerM2 = basicModule * geographic * typeOfWorks * quality * cu;
  const facadeArea = area * params.facadeRatio[propertyType];
  const budget = round((facadeArea * costPerM2) / 100, 0) * 100;

  // 4. Current consumption: the certificate value if entered, else the representative value
  const scale = epcScale(propertyType, params);
  const certificateValue = Number(currentConsumption);
  const fromCertificate = currentConsumption !== "" && currentConsumption != null && certificateValue > 0;
  const consumption = fromCertificate ? certificateValue : scale.representative[currentRating];

  // 5–6. Projected consumption and rating for each scenario
  const projected = Object.fromEntries(
    Object.entries(params.scenarios).map(([key, saving]) => {
      const value = consumption * (1 - saving);
      return [key, { saving, consumption: value, rating: ratingFor(value, propertyType, params) }];
    })
  );

  return {
    propertyType,
    floorArea: area,
    dwellingUseCoefficient: cu,
    costPerM2,
    facadeArea,
    budget,
    currentRating,
    consumption,
    fromCertificate,
    // Rating the entered certificate value falls in (it may differ from the letter given)
    certificateRating: fromCertificate ? ratingFor(certificateValue, propertyType, params) : null,
    projected,
    // 7. The works are not expected to change the rating letter
    noChange: Object.values(projected).every((p) => p.rating === currentRating),
    limits: scale.limits,
    parameterVersion: params.version,
  };
}
